/* assets/kaileep-pdp-wrapper.js */
/* PDP redesign scripts. Active when <html> carries .kaileep-site-wrapper
   (set server-side in layout/theme.liquid). Styles: assets/kaileep-pdp-wrapper.css */

/* ============================================================
   1. Mobile header move
   ============================================================
   On <=749px, moves the product header block (title / style / reviews / price)
   out of the right-hand details column and inserts it directly above the mobile
   media carousel; moves it back to its original spot on desktop. Re-runs on
   viewport resize and if the .kaileep-site-wrapper class is toggled on <html>. */
document.addEventListener("DOMContentLoaded", () => {
  const headerBlock = document.querySelector(
    ".product__block.product__block--product-header.product__block--with-divider"
  );

  const mediaContainer = document.querySelector(
    ".product__media-container.below-mobile.carousel.swiper"
  );

  if (!headerBlock || !mediaContainer) return;

  const originalParent = headerBlock.parentNode;
  const originalNextSibling = headerBlock.nextElementSibling;
  const mobileQuery = window.matchMedia("(max-width: 749px)");

  function moveHeaderIfNeeded() {
    const isActive = document.documentElement.classList.contains("kaileep-site-wrapper");

    if (isActive && mobileQuery.matches) {
      if (headerBlock.parentNode !== mediaContainer.parentNode || headerBlock.nextElementSibling !== mediaContainer) {
        mediaContainer.parentNode.insertBefore(headerBlock, mediaContainer);
      }
    } else {
      if (headerBlock.parentNode !== originalParent || headerBlock.nextElementSibling !== originalNextSibling) {
        if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
          originalParent.insertBefore(headerBlock, originalNextSibling);
        } else {
          originalParent.appendChild(headerBlock);
        }
      }
    }
  }

  // 1. Check immediately
  moveHeaderIfNeeded();

  // 2. Watch for screen size changes
  mobileQuery.addEventListener("change", moveHeaderIfNeeded);

  // 3. Watch for the wrapper class being toggled on <html>
  const observer = new MutationObserver(() => {
    moveHeaderIfNeeded();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
});

/* ============================================================
   2. Title-case option text
   ============================================================
   Converts the option label text ("STYLE:" -> "Style:"), the selected value span,
   and every <option> in the variant <select>s to Title Case. Each node is tagged
   data-kaileep-formatted so it is only converted once; values written later by
   theme.js on variant change are left as-is. A childList observer on <body>
   (rAF-debounced) covers option markup injected after load, e.g. the quick-view modal. */
document.addEventListener("DOMContentLoaded", () => {
  if (!document.documentElement.classList.contains("kaileep-site-wrapper")) return;

  const toTitleCase = (str) =>
    (str || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const format = (el, fn) => {
    if (el.dataset.kaileepFormatted) return;
    fn(el);
    el.dataset.kaileepFormatted = "true";
  };

  const formatOptions = () => {
    document.querySelectorAll(".product__option .product__label").forEach((label) =>
      format(label, () => {
        const textNode = Array.from(label.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
        );
        if (textNode) textNode.textContent = " " + toTitleCase(textNode.textContent.trim().replace(":", "")) + ": ";

        const selected = label.querySelector("[data-selected-value-for-option]");
        if (selected) selected.textContent = toTitleCase(selected.textContent);
      })
    );

    document.querySelectorAll(".product__option select option").forEach((option) =>
      format(option, () => (option.textContent = toTitleCase(option.textContent)))
    );
  };

  let queued = false;
  const scheduleFormat = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      formatOptions();
    });
  };

  formatOptions();
  new MutationObserver(scheduleFormat).observe(document.body, { childList: true, subtree: true });
});

/* ============================================================
   3. Kiwi size-chart label row
   ============================================================
   Rebuilds each .product__label-wrapper that contains a Kiwi size chart
   (.ks-chart-container): option label + "Size Chart" button on one row, the
   Kiwi fit note (e.g. "Runs Small") as a badge underneath. The original label
   and Kiwi container are hidden, not removed, so Kiwi's own click handler
   still opens the modal. Kiwi injects late, so a body observer + a few
   delayed retries cover it. Styles: kaileep-pdp-wrapper.css §27. */
document.addEventListener("DOMContentLoaded", () => {
  if (!document.documentElement.classList.contains("kaileep-site-wrapper")) return;

  const toTitleCase = (str) =>
    (str || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bUs\b/g, "US");

  const enhance = (wrapper) => {
    const label = wrapper.querySelector(".product__label");
    const value = label?.querySelector("[data-selected-value-for-option]");
    const chart = wrapper.querySelector(".ks-chart-container");
    const link = chart?.querySelector(".ks-chart-modal-link");
    const text = chart?.querySelector("._ks_text");
    if (!label || !value || !chart || !link || !text) return;

    let row = wrapper.querySelector(".kaileep-pdp-label-row");
    if (!row) {
      row = document.createElement("div");
      row.className = "kaileep-pdp-label-row";
      row.innerHTML = '<div class="kaileep-pdp-option-label"></div><button type="button" class="kaileep-pdp-size-chart-link"></button>';
      row.querySelector("button").addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); link.click(); });
      wrapper.insertBefore(row, wrapper.firstChild);
    }

    const name = toTitleCase((label.childNodes[0]?.textContent || "").replace(":", "").trim());
    const selected = (value.textContent || "").trim();
    const set = (el, prop, v) => { if (el[prop] !== v) el[prop] = v; }; // write only on change: the body observer would otherwise re-fire
    set(row.querySelector(".kaileep-pdp-option-label"), "innerHTML",
      `<span class="kaileep-pdp-option-label-value">${name}:</span>${selected ? " " + selected : ""}`);
    set(row.querySelector("button"), "textContent", toTitleCase(text.textContent || "Size Chart"));

    const fit = toTitleCase((chart.querySelector(".ks-chart-modal-link-suffix")?.textContent || "").replace(/^\s*\|\s*/, "").trim());
    let badge = wrapper.querySelector(".kaileep-pdp-fit-badge");
    if (fit) {
      if (!badge) {
        badge = document.createElement("div");
        badge.className = "kaileep-pdp-fit-badge";
        wrapper.appendChild(badge);
      }
      set(badge, "textContent", fit);
    } else if (badge) badge.remove();

    label.style.display = "none";
    chart.style.display = "none";
    wrapper.classList.add("kaileep-pdp-label-wrapper");
  };

  let queued = false;
  const sync = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll(".product__label-wrapper").forEach(enhance);
    });
  };

  sync();
  new MutationObserver(sync).observe(document.body, { childList: true, subtree: true, characterData: true });
  [300, 800, 1500, 2500].forEach((ms) => setTimeout(sync, ms));
});
